import dynamic from "next/dynamic";
import { useMemo, useContext } from "react";
import { useSelector } from "react-redux";

import Loaders from "@stadium/ui/src/components/loaders/loaders";

import { InternalButton, ExternalButton } from "@sharedComponents/buttons";

import {
  getCartItemsCount,
  getCartOrderId,
  getCartOrderLineItems,
  getCartOrderMedleySets,
  getCartOrderProductSets,
  getCartOrderCurrency,
  getIsCartUpdating,
} from "@store/selectors/cart";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import { generateLineItemsMappedData } from "@helpers/orders/cart";

import { useStoreContext } from "@components/store/provider";
import { usePreviewModeContext } from "@contexts/preview-mode-context";

import CartProductListing from "@components/shared/cart/send-to-many/cart-product-listing";

import useConfirmationModal from "@hooks/cart/use-confirmation-modal";
import usePopulateItems from "@hooks/cart/use-populate-items";
import useBatchPopulateItems from "@hooks/cart/use-batch-populate-items";
import useManipulateCartProduct from "@hooks/cart/use-manipulate-cart-product";

import { removeItemsConfirmation } from "@constants/confirmation-modal";
import { getCartRoute } from "@constants/routes";

import { PURCHASING_SWAG_NOTE, ORDER_TYPES } from "@constants/orders/cart";
import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";
import { ANALYTICS_EVENT_ACTION_CLEARED } from "@stadium/services/analytics/constants/default";

import styles from "../main.module.scss";

const SwagCartNote = dynamic(() => import("@components/shared/cart/swag-cart-note"), {
  ssr: false,
});

const emptyObj = {};

const SendToMyselfCartListing = ({
  editable = true,
  className = "",
  showEditBagCTA = true,
  cartProductsData = emptyObj,
}) => {
  const lineItems = useSelector(getCartOrderLineItems);
  const productSets = useSelector(getCartOrderProductSets);
  const medleySets = useSelector(getCartOrderMedleySets);
  const orderCurrencyCode = useSelector(getCartOrderCurrency);
  const isCartUpdating = useSelector(getIsCartUpdating);

  const orderId = useSelector(getCartOrderId);
  const itemsCount = useSelector(getCartItemsCount);
  const withAnalytics = useWithAnalytics();
  const { storeBasePath } = useStoreContext();
  const { isGuestOrPreviewMode } = usePreviewModeContext();

  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const { variantsMapping, variantDataloading } = cartProductsData;

  const { toggleConfirmationModal, renderConfirmationModal } =
    useConfirmationModal(removeItemsConfirmation);

  const {
    emptyingCart,
    onProductChangeQuantity,
    removeProductFromCart,
    removeAllProductsFromCart,
  } = useManipulateCartProduct({ emptyCartSuccessCb: toggleConfirmationModal });

  const { loading: shopProductLoading } = usePopulateItems();
  const { loading: bulkProductLoading } = useBatchPopulateItems({
    type: ORDER_TYPES.organizer,
  });

  const loading = shopProductLoading || bulkProductLoading;

  const mappedLineItems = useMemo(
    () =>
      generateLineItemsMappedData({
        lineItems,
        variantsMapping,
        variantDataloading,
        isGuestOrPreviewMode,
      }),
    [lineItems, variantsMapping, variantDataloading, isGuestOrPreviewMode],
  );

  const disabled = emptyingCart || loading;

  const removeAllItemsHandler = () => {
    withAnalytics(undefined, {
      track: true,
      action: ANALYTICS_EVENT_ACTION_CLEARED,
      eventProperties: { total_items: itemsCount },
    })();
    removeAllProductsFromCart(orderId);
  };

  const renderRemoveAllBtn = () => {
    if (!editable) return null;
    return (
      <>
        <div className={styles.colAutoLink}>
          <InternalButton
            styleType="no-style"
            className={styles.removeLink}
            onClick={toggleConfirmationModal}
          >
            REMOVE ALL ITEMS
          </InternalButton>
        </div>
        {renderConfirmationModal({
          loading: emptyingCart,
          onConfirmHandler: removeAllItemsHandler,
        })}
      </>
    );
  };

  const renderEditBagCta = () =>
    showEditBagCTA && (
      <ExternalButton
        className={styles.editLink}
        href={getCartRoute({ storeBasePath })}
        styleType="link"
      >
        Edit Bag
      </ExternalButton>
    );

  if (variantDataloading || isCartUpdating)
    return (
      <div>
        <Loaders.PageLoader wrapperClass="pageLoaderwrap min-h-150" />
      </div>
    );

  return (
    <div className={className}>
      <CartProductListing
        lineItems={mappedLineItems}
        medleySets={medleySets}
        productSets={productSets}
        commonProps={{
          disabled,
          editable,
          onProductChangeQuantity,
          removeProductFromCart,
          variantsMapping,
          orderCurrencyCode,
        }}
      />
      {renderRemoveAllBtn()}
      {/* Edit bag CTA needs to be shown on checkout screen only. editable is false on checkout screen */}
      {!editable && renderEditBagCta()}

      {isMobileDevice && <SwagCartNote noteText={PURCHASING_SWAG_NOTE} />}
    </div>
  );
};

export default SendToMyselfCartListing;
